import { Purchase } from '@domain'
import useFiltering from '@hooks/useFiltering'
import { MarketBasketsKeys } from '@services/marketBaskets.api'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { NumberParam, StringParam, withDefault } from 'use-query-params'

import { Statuses } from './components/PurchasesTable/PurchasesTable.utils'

/** Updates purchase summaries in the list cache and invalidates detail/kpi queries */
export function useUpdatePurchasesCache() {
	const { marketBasketId } = useParams()
	const queryClient = useQueryClient()

	return (updatedSummaries: Purchase[]) => {
		if (!updatedSummaries?.length) return

		const updatedById = new Map(updatedSummaries.map((s) => [s.id, s]))

		// Invalidate individual purchase detail queries
		updatedById.forEach((_, id) => {
			queryClient.invalidateQueries({
				queryKey: [...MarketBasketsKeys.marketBasket(marketBasketId), 'purchase', { groupKey: id }],
			})
		})

		// Invalidate kpis
		queryClient.invalidateQueries({
			queryKey: [...MarketBasketsKeys.marketBasket(marketBasketId), 'kpis'],
		})

		// Update purchase summaries in the purchases list cache
		queryClient.setQueriesData<InfiniteData<PaginatedAPIRes<Purchase[]>>>(
			{ queryKey: [...MarketBasketsKeys.marketBasket(marketBasketId), 'purchases'] },
			(old) => {
				if (!old?.pages) return old
				return {
					...old,
					pages: old.pages.map((page) => ({
						...page,
						data: page.data.map((p) => updatedById.get(p.id) ?? p),
					})),
				}
			}
		)
	}
}

export const useBasketFilters = () =>
	useFiltering(
		{
			group: withDefault(StringParam, null),
			category: withDefault(NumberParam, null),
			manufacturer: withDefault(NumberParam, null),
			search: withDefault(StringParam, null),
			brandTypePurchase: withDefault(NumberParam, null),
			brandTypeMatch: withDefault(NumberParam, null),
			exclusion: withDefault(StringParam, null),
			packSize: withDefault(StringParam, null),
			matchType: withDefault(StringParam, null),
			sameManufacturerSwitch: withDefault(StringParam, null),
			qty: withDefault(StringParam, null),
			extendedPrice: withDefault(StringParam, null),
			status: withDefault(StringParam, Statuses.Review),
			reviewType: withDefault(StringParam, null),
		},
		{ resetPageOnChange: true, pageKey: 'page' }
	)
